import React, {useEffect, useState} from 'react';
import {Alert, Button, Card, Col, Container, Form, Modal, OverlayTrigger, Row, Spinner, Tooltip} from 'react-bootstrap';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {BsCheckCircle, BsExclamationTriangle, BsXCircle} from 'react-icons/bs';

import infoCircle from "../../../assets/img/info-circle.svg";
import logo from "../../../assets/img/dmarc-flow-logo.webp";
import ScoreChart from "./ScoreChart";
import BimiResult from "../BimiResult";
import hostname from "../../../hostname";

import './AnalyzeResults.css';
import {routes} from "../../../routes";
import {I18nService} from "../../../service/i18nService";

const AnalyzeResults = () => {
    const {t, i18n} = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [showModal, setShowModal] = useState(false);
    const [showPdfModal, setShowPdfModal] = useState(false);
    const [pdfEmail, setPdfEmail] = useState('');
    const [form, setForm] = useState({name: '', email: '', password: ''});
    const [formState, setFormState] = useState({loading: false, error: '', success: false});

    const validation = useSelector(state => state.validation);
    const data = validation?.payload || {};
    const {dmarc = [], spf = [], dkim = [], bimi = [], domain = '', date = {}, riskAssesment = {}, score = 0} = data;

    const i18nService = new I18nService();
    const language = i18nService.getLanguage();

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const domainParam = params.get('domain');
        if (domainParam) {
            fetch(`${hostname}/index.php/domain/analyze/${domainParam}`)
                .then(res => res.json())
                .then(json => dispatch({type: 'SET_VALIDATION_RESULT', payload: json}))
                .catch(console.error);
        }
    }, [dispatch]);

    if (!validation?.payload) return <div className="loading-screen">{t('loading')}</div>;

    const borderColor = {
        'NOT_SUBSCRIBED': 'secondary', 'VERY_LOW': 'success', 'LOW': 'success',
        'MODERATE': 'warning', 'HIGH': 'danger', 'CRITICAL': 'danger'
    }[riskAssesment.key] || 'primary';

    const handleGetPdf = (email, domainParam) => {

        fetch(
            hostname + '/index.php/domain/analyze/result/pdf',
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: "Bearer eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJpc3MiOiJ1Y2Fyc29sdXRpb25zIiwiYXVkIjp7InR5cGUiOiJ1Y2Fyc29sdXRpb25zIiwidmFsdWUiOiI2ZGYxZGNiZS00Yzk2LTQyN2UtOTMwNy02MjNlODkzOWI2ODQifSwiaWF0IjoxNzUwNDI4MzE5LCJuYmYiOjE3NTA0MjgzMTh9.1pL9dfI10xsCNTRIPsDevHmXhlPtzGFrHB_fYLCCqko"
                },
                body: JSON.stringify({email: email.trim(), domain: domainParam}),
            })
            .then(response => {
                if (!response.ok) {
                    console.warn('Server returned an error:', response.status);
                    return;
                }
                setShowPdfModal(false);
            })
            .catch(error => {
                console.error('Error during API call:', error);
            });
    }

    const handleRegister = async () => {
        setFormState({...formState, loading: true, error: '', success: false});
        try {
            const res = await fetch(`${hostname}/index.php/register`, {
                method: 'POST', headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(form)
            });
            if (!res.ok) throw new Error(t('pages.analyzeResults.modal.form.registerFail'));
            setFormState({loading: false, success: true, error: ''});
            setTimeout(() => setShowModal(false), 2000);
        } catch (err) {
            setFormState({...formState, loading: false, error: err.message});
        }
    };

    const getTooltip = (text) => (
        <Tooltip>{text}</Tooltip>
    );

    const getStatusIcon = (protocol) => {
        for (const p of protocol) {
            if (p.issues?.length > 0) return <BsXCircle size={24} className="text-danger"/>;
            if (p.warnings?.length > 0) return <BsExclamationTriangle size={24} className="text-warning"/>;
        }
        return <BsCheckCircle size={24} className="text-success"/>;
    };

    const getBorderClass = (protocol) => {
        for (const p of protocol) {
            if (p.issues?.length > 0) return "border border-danger-subtle";
            if (p.warnings?.length > 0) return "border border-warning-subtle";
        }
        return "border border-success-subtle";
    };

    const renderProtocol = (title, protocol) => (
        <Col md={4} className="mb-3">
            <Card className={getBorderClass(protocol)}>
                <Card.Body className="text-center">
                    <h5 className="d-flex justify-content-center align-items-center gap-2">
                        {getStatusIcon(protocol)} {t(`pages.analyzeResults.${title}.title`)}
                    </h5>
                    <p className="small text-muted mt-2 mb-0">{t(`pages.analyzeResults.${title}.hint`)}</p>
                </Card.Body>
            </Card>
        </Col>
    );

    return (
        <Container className="analyze-results py-4">
            <Alert variant="danger" className="text-center fw-bold">
                🚨 {t('pages.analyzeResults.fomoAlert', {domain})} <Button variant="outline-dark" size="sm"
                                                                          onClick={() => setShowModal(true)}>{t('pages.analyzeResults.secureNow')}</Button>
            </Alert>

            <h2 className="text-center mb-4">
                {t('pages.analyzeResults.scanningResults')} <strong>{domain}</strong>
            </h2>

            <section className="mb-5">
                <h4 className="mb-3 text-primary">{t('pages.analyzeResults.riskAssessmentLevel')} {riskAssesment.value}</h4>
                <Card border={borderColor} className="shadow-sm">
                    <Card.Body>
                        <p><strong>{t('pages.analyzeResults.overallSecurity')}</strong>: {score} / 10</p>
                        <p className="text-muted">{riskAssesment.description}</p>
                        <Button variant="primary" className={"text-white"}
                                onClick={() => () => {
                                    navigate(
                                        language + "/" + routes["pricing"][language]
                                    )
                                }}>{t('pages.analyzeResults.subscribeNow')}</Button>
                    </Card.Body>
                </Card>
            </section>

            <section className="mb-5">
                <h4 className="mb-3 text-primary d-flex align-items-center">
                    {t('pages.analyzeResults.overallResult')}
                    <OverlayTrigger placement="right"
                                    overlay={getTooltip(t('pages.analyzeResults.tooltip.description'))}>
                        <img src={infoCircle} alt="Info" width="20" className="ms-2"/>
                    </OverlayTrigger>
                </h4>
                <ScoreChart score={score} maxScore={10}/>
            </section>

            <section className="mb-5">
                <h4 className="mb-3 text-primary">{t('pages.analyzeResults.protocols.title')}</h4>
                <Row>
                    {renderProtocol('dmarc', dmarc)}
                    {renderProtocol('spf', spf)}
                    {renderProtocol('dkim', dkim)}
                </Row>

                <BimiResult bimiData={bimi}/>
            </section>

            <section className="mb-5 text-center">
                <h4 className="mb-3 text-primary">{t('pages.analyzeResults.whatsNext.title')}</h4>
                <p className="lead">{t('pages.analyzeResults.whatsNext.description')}</p>
                <Button variant="primary" className={"text-white"} size="lg" onClick={
                    () => {
                        navigate(
                            language + "/" + routes["pricing"][language]
                        )
                    }
                }>
                    {t('pages.analyzeResults.whatsNext.buttonText')}
                </Button>
            </section>

            <section className="mb-5 text-center text-muted">
                <hr/>
                <p><strong>{t('pages.analyzeResults.stat')}</strong> {t('pages.analyzeResults.fomoFact')}</p>
                <Button variant="outline-secondary" size="sm"
                        onClick={() => setShowPdfModal(true)}>{t('pages.analyzeResults.downloadPdf')}</Button>
            </section>

            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                <Modal.Header closeButton>
                    <img src={logo} alt="Logo" className="me-2" height="40"/>
                    <Modal.Title>{t('pages.analyzeResults.modal.title')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {formState.error && <Alert variant="danger">{formState.error}</Alert>}
                    {formState.success && <Alert variant="success">{t('pages.analyzeResults.modal.success')}</Alert>}
                    <Form>
                        {['name', 'email', 'password'].map(field => (
                            <Form.Group key={field} className="mb-3">
                                <Form.Label>{t(`pages.analyzeResults.modal.form.${field}`)}</Form.Label>
                                <Form.Control
                                    type={field === 'password' ? 'password' : 'text'}
                                    value={form[field]}
                                    onChange={e => setForm({...form, [field]: e.target.value})}
                                    placeholder={t(`pages.analyzeResults.modal.form.${field}`)}
                                />
                            </Form.Group>
                        ))}
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={handleRegister} disabled={formState.loading}
                            className="text-white">
                        {formState.loading ?
                            <Spinner animation="border" size="sm"/> : t('pages.analyzeResults.modal.form.register')}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showPdfModal} onHide={() => setShowPdfModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{t('pages.analyzeResults.downloadPdfModal.title')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="mb-3">{t('pages.analyzeResults.downloadPdfModal.description')}</p>
                    <Form>
                        <Form.Group>
                            <Form.Label>{t('pages.analyzeResults.downloadPdfModal.emailLabel')}</Form.Label>
                            <Form.Control
                                type="email"
                                placeholder="you@example.com"
                                value={pdfEmail}
                                onChange={(e) => setPdfEmail(e.target.value)}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" className={"text-white"} onClick={() => setShowPdfModal(false)}>
                        {t('cancel')}
                    </Button>
                    <Button variant="primary" className={"text-white"} onClick={() => {
                        handleGetPdf(pdfEmail, domain);
                    }}>
                        {t('pages.analyzeResults.downloadPdfModal.sendButton')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default AnalyzeResults;
